import { Dispatch, SetStateAction, useEffect, useState } from "react";

type LocalStorageStateOptions<T> = {
  deserialize?: (storedValue: unknown, fallback: T) => T;
  serialize?: (value: T) => unknown;
};

export type StoredStateRecord = Record<string, unknown>;

const hasLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

export const isStoredStateRecord = (
  value: unknown,
): value is StoredStateRecord => typeof value === "object" && value !== null;

export const getStoredString = (
  record: StoredStateRecord,
  key: string,
  fallback: string,
) => {
  const value = record[key];
  return typeof value === "string" ? value : fallback;
};

export const getStoredNumber = (
  record: StoredStateRecord,
  key: string,
  fallback: number,
) => {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fallback;
};

export const getStoredBoolean = (
  record: StoredStateRecord,
  key: string,
  fallback: boolean,
) => {
  const value = record[key];
  return typeof value === "boolean" ? value : fallback;
};

export const getStoredStringArray = (
  record: StoredStateRecord,
  key: string,
  fallback: string[],
) => {
  const value = record[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : fallback;
};

export const getStoredDate = (
  record: StoredStateRecord,
  key: string,
  fallback: Date | undefined,
) => {
  const value = record[key];

  if (typeof value !== "string") {
    return fallback;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date;
};

export function readLocalStorageState<T>(
  key: string,
  fallback: T,
  deserialize?: (storedValue: unknown, fallback: T) => T,
): T {
  if (!hasLocalStorage()) {
    return fallback;
  }

  const storedValue = window.localStorage.getItem(key);

  if (storedValue === null) {
    return fallback;
  }

  try {
    let parsedValue: unknown;

    try {
      parsedValue = JSON.parse(storedValue) as unknown;
    } catch {
      parsedValue = storedValue;
    }

    return deserialize ? deserialize(parsedValue, fallback) : (parsedValue as T);
  } catch (error) {
    console.error(`Failed to read ${key} from localStorage`, error);
    return fallback;
  }
}

export function writeLocalStorageState(key: string, value: unknown): void {
  if (!hasLocalStorage()) {
    return;
  }

  try {
    if (value === undefined) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to write ${key} to localStorage`, error);
  }
}

export function removeLocalStorageState(key: string): void {
  if (!hasLocalStorage()) {
    return;
  }

  window.localStorage.removeItem(key);
}

export function useLocalStorageState<T>(
  key: string,
  fallback: T,
  options: LocalStorageStateOptions<T> = {},
): [T, Dispatch<SetStateAction<T>>] {
  const { deserialize, serialize } = options;
  const [state, setState] = useState<T>(() =>
    readLocalStorageState(key, fallback, deserialize),
  );

  useEffect(() => {
    writeLocalStorageState(key, serialize ? serialize(state) : state);
  }, [key, serialize, state]);

  return [state, setState];
}
